import { Service } from "typedi";
import { ConfigProvider } from "../../../shared/services/config-provider/ConfigProvider";
import { IGooglePayPaymentRequest } from "../../../integrations/google-pay/models/IGooglePayPaymentRequest";
import { hasDynamicPriceIntent } from "./GooglePayNativeCallbackIntents";

/**
 * Gives merchants a hook to update transaction info before the wallet sheet opens,
 * without requiring full shipping/offer configuration.
 *
 * When a merchant provides onPaymentDataChanged but no dynamic price intents
 * (SHIPPING_OPTION, SHIPPING_ADDRESS, OFFER), Google Pay won't invoke it.
 * This handler simulates the INITIALIZE trigger so the merchant can still
 * adjust pricing, line items, or shipping options before the sheet renders.
 *
 * If dynamic price intents ARE configured, Google Pay handles the callback
 * natively and this handler is skipped.
 */
@Service()
export class GooglePayPrePaymentCallbackHandler {
  constructor(private configProvider: ConfigProvider) {}

  isRequired(paymentDataRequest: IGooglePayPaymentRequest): boolean {
    return (
      !!this.getOnPaymentDataChanged() &&
      !hasDynamicPriceIntent(paymentDataRequest.callbackIntents)
    );
  }

  async trigger(
    paymentDataRequest: IGooglePayPaymentRequest,
  ): Promise<IGooglePayPaymentRequest> {
    const callback = this.getOnPaymentDataChanged();

    if (!callback) {
      return paymentDataRequest;
    }

    const result = await Promise.resolve(
      callback({
        callbackTrigger: "INITIALIZE",
        paymentMethodData: { type: "CARD" },
      }),
    );

    if (!result.newTransactionInfo) {
      return paymentDataRequest;
    }

    return {
      ...paymentDataRequest,
      transactionInfo: {
        ...paymentDataRequest.transactionInfo,
        ...result.newTransactionInfo,
      },
    };
  }

  private getOnPaymentDataChanged() {
    return this.configProvider.getConfig().googlePay?.paymentRequest
      ?.paymentDataCallbacks?.onPaymentDataChanged;
  }
}
